import React, { useEffect, useState, useMemo } from 'react';
import { employeeWalletAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Wallet, Send, FileText, Gift, ArrowUpRight, ArrowDownLeft, Loader2,
  UtensilsCrossed, Bus, Heart, Shield, ChevronRight
} from 'lucide-react';

const fmt = (n) => `₦${Math.round(Number(n || 0)).toLocaleString()}`;
const CATEGORY_ICONS = { food: UtensilsCrossed, transport: Bus, health: Heart, insurance: Shield };
const CATEGORY_COLORS = { food: '#f59e0b', transport: '#ec4899', health: '#00B894', insurance: '#8b5cf6' };

export default function EmployeeHome() {
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [comp, setComp] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [p, w, c, b, t] = await Promise.all([
          employeeWalletAPI.getProfile(),
          employeeWalletAPI.getBalance(),
          employeeWalletAPI.getCompensation(),
          employeeWalletAPI.getBenefitBalances(),
          employeeWalletAPI.getTransactions(),
        ]);
        setProfile(p); setWallet(w); setComp(c); setBenefits(b); setTxns(t.slice(0, 5));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  const quickActions = [
    { icon: Send, label: 'Send', path: '/app/employee/wallet/send', color: 'bg-pink-50 text-pink-600' },
    { icon: Gift, label: 'Vendors', path: '/app/employee/benefits/vendors', color: 'bg-amber-50 text-amber-600' },
    { icon: FileText, label: 'Pay Slip', path: '/app/employee/pay', color: 'bg-blue-50 text-blue-600' },
    { icon: Gift, label: 'Benefits', path: '/app/employee/benefits', color: 'bg-emerald-50 text-emerald-600' },
  ];

  return (
    <div className="space-y-5 pb-6" data-testid="employee-home">
      <p className="text-base text-gray-500">{greeting}, <span className="font-semibold text-gray-900">{profile?.first_name}</span></p>

      {/* Wallet Card */}
      <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-0 text-white shadow-lg" data-testid="wallet-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Wallet Balance</span>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 text-xs h-7 px-3" onClick={() => navigate('/app/employee/wallet/send')}>
              Send Money <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
          <p className="text-3xl font-bold mb-3" style={{ fontFamily: 'JetBrains Mono' }}>{fmt(wallet?.balance)}</p>
          <div className="flex gap-4 text-xs">
            {benefits.filter(b => !b.is_auto).map(b => {
              const Icon = CATEGORY_ICONS[b.category] || Gift;
              return (
                <div key={b.category} className="flex items-center gap-1.5 text-gray-300">
                  <Icon className="w-3 h-3" style={{ color: CATEGORY_COLORS[b.category] }} />
                  <span className="capitalize">{b.category}</span>
                  <span className="font-semibold text-white">{fmt(b.remaining)}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-4 gap-3" data-testid="quick-actions">
        {quickActions.map(a => (
          <button key={a.label} onClick={() => navigate(a.path)} className="flex flex-col items-center gap-1.5 py-3 rounded-xl hover:bg-gray-50 transition-colors">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${a.color}`}>
              <a.icon className="w-4.5 h-4.5" />
            </div>
            <span className="text-[11px] font-medium text-gray-600">{a.label}</span>
          </button>
        ))}
      </div>

      {/* Compensation Flow */}
      {comp && (
        <Card className="border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/app/employee/pay')} data-testid="compensation-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Your Compensation This Month</span>
              <span className="text-xs text-pink-500 font-medium">{fmt(comp.total_package)} Total <ChevronRight className="w-3 h-3 inline" /></span>
            </div>
            <div className="space-y-1.5">
              {[
                { label: 'Your bank account', amount: comp.cash_to_bank, color: '#00B894', bold: true },
                { label: 'Pension (employer)', amount: comp.employer_pension, color: '#6366f1' },
                { label: 'Pension (you)', amount: comp.employee_pension, color: '#6366f1' },
                ...(comp.benefits ? Object.entries(comp.benefits).map(([k, v]) => ({ label: k.charAt(0).toUpperCase() + k.slice(1), amount: v, color: CATEGORY_COLORS[k] || '#9ca3af' })) : []),
                { label: 'PAYE tax', amount: comp.paye_tax, color: '#6b7280' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-0.5">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className={`text-xs ${item.bold ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{item.label}</span>
                  </div>
                  <span className={`text-xs font-semibold ${item.bold ? 'text-emerald-600' : 'text-gray-900'}`} style={{ fontFamily: 'JetBrains Mono' }}>{fmt(item.amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <div data-testid="recent-activity">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Recent Activity</span>
          <button onClick={() => navigate('/app/employee/transactions')} className="text-xs text-pink-500 font-medium">View all <ChevronRight className="w-3 h-3 inline" /></button>
        </div>
        <Card className="border-gray-200 shadow-sm">
          <CardContent className="p-0 divide-y divide-gray-100">
            {txns.length === 0 && <p className="p-4 text-xs text-gray-400 text-center">No recent transactions</p>}
            {txns.map((tx, i) => {
              const isCredit = tx.type?.includes('credit') || tx.destination_wallet_id;
              const cat = tx.category || '';
              const CatIcon = CATEGORY_ICONS[cat] || (isCredit ? ArrowDownLeft : ArrowUpRight);
              return (
                <div key={i} className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isCredit ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                      <CatIcon className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-900">{tx.description || tx.narration || 'Transaction'}</p>
                      <p className="text-[10px] text-gray-400">{tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }) : ''}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-bold ${isCredit ? 'text-emerald-600' : 'text-gray-900'}`} style={{ fontFamily: 'JetBrains Mono' }}>
                    {isCredit ? '+' : '-'}{fmt(tx.amount)}
                  </span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
